import React, { useCallback } from 'react';
import { Col, Row, Typography, Space, Button, Spin, notification } from 'antd';
import { Form, useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { Field } from '../../../components/form/field';
import { useTranslatedOptions } from '../../../components/form/hooks/use-translated-options';
import { SelectField } from '../../../components/form/select-field';
import { DateField } from '../../../components/form/date-field';
import { TextAreaField } from '../../../components/form/text-area-field';
import { CheckboxField } from '../../../components/form/checkbox-field';
import { UploadField } from '../../../components/form/upload-field';
import { Result } from './result';
import styles from './styles.module.css';
import { ManagerField } from './manager-field';

// eslint-disable-next-line react/jsx-props-no-spreading
const DefaultRow = (props) => <Row {...props} gutter={24} />;

const DEPARTMENTS = [
  { label: 'scaffolding', value: 'scaffolding' },
  { label: 'insulation', value: 'insulation' },
  { label: 'manufacturing', value: 'manufacturing' },
  { label: 'administration', value: 'administration' },
];

const SUBMIT_STATUS = {
  SUCCESS: 'submitted',
  ERROR: 'error',
};

const hasErrors = (errors) => Object.keys(errors).length !== 0;
const touchAllFields = (fields = {}) =>
  Object.keys(fields).reduce((acc, key) => {
    return {
      ...acc,
      [key]: true,
    };
  }, {});

export function FormLayout() {
  const { t } = useTranslation();
  const {
    values,
    resetForm,
    isSubmitting,
    status,
    submitForm,
    validateForm,
    setTouched,
    setStatus,
  } = useFormikContext();
  const options = useTranslatedOptions(DEPARTMENTS, 'field.department.options');
  const handleSubmitAgain = useCallback(() => {
    resetForm();
  }, []);

  const handleSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      setTouched(touchAllFields(values), true);
      const errorsObject = await validateForm();

      if (hasErrors(errorsObject)) {
        return notification.error({
          message: t('submit.validation-error'),
          description: t('submit.validation-error-message'),
        });
      }

      try {
        const result = await submitForm();
        setStatus(SUBMIT_STATUS.SUCCESS);
        return Promise.resolve(result);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    [submitForm, validateForm, setStatus]
  );

  const isSubmitted = status === SUBMIT_STATUS.SUCCESS;

  return (
    <div>
      {isSubmitted ? (
        <Result onSubmitAgain={handleSubmitAgain} />
      ) : (
        <Form>
          <DefaultRow>
            <Col md={12} span={24}>
              <Field name="author" />
              <DateField name="date" />
              <ManagerField />
            </Col>
            <Col md={12} span={24}>
              <SelectField name="department" options={options} />
              <Field name="email" />
              <Field name="phoneNumber" />
            </Col>
          </DefaultRow>
          <DefaultRow>
            <Col md={12} span={24}>
              <Typography.Title level={5}>
                {t('title.problem')}
              </Typography.Title>
              <TextAreaField name="description" rows={9} />
            </Col>
            <Col md={12} span={24}>
              <Typography.Title level={5}>
                {t('title.benefits')}
              </Typography.Title>
              <DefaultRow>
                <Col md={12} span={24}>
                  <Space direction="vertical" size={6}>
                    <Typography.Text>{t('title.marking')}</Typography.Text>
                    <CheckboxField name="safety" />
                    <CheckboxField name="quality" />
                  </Space>
                </Col>
                <Col md={12} span={24}>
                  <Space direction="vertical" size={6}>
                    <Typography.Text>{t('title.decreaseLoss')}</Typography.Text>
                    <CheckboxField name="movement" />
                    <CheckboxField name="waiting" />
                    <CheckboxField name="transportation" />
                    <CheckboxField name="correction" />
                    <CheckboxField name="additionalProcess" />
                    <CheckboxField name="excessProduction" />
                    <CheckboxField name="reservesLoss" />
                  </Space>
                </Col>
              </DefaultRow>
            </Col>
          </DefaultRow>
          <DefaultRow>
            <Col md={12} span={24}>
              <Typography.Title level={5}>
                {t('title.current-situation')}
              </Typography.Title>
              <TextAreaField name="currentSituationDescription" rows={9} />
              <UploadField name="currentSituationImages" />
            </Col>
            <Col md={12} span={24}>
              <Typography.Title level={5}>
                {t('title.future-situation')}
              </Typography.Title>
              <TextAreaField name="futureSituationDescription" rows={9} />
              <UploadField name="futureSituationImages" />
            </Col>
          </DefaultRow>
          <DefaultRow>
            <Col md={12} span={24}>
              <Typography.Title level={5}>
                {t('title.five-why')}
              </Typography.Title>
              <TextAreaField name="mainProblemFix" rows={10} />
            </Col>
            <Col md={12} span={24}>
              <Typography.Title level={5}>
                {t('title.your-standardisation')}
              </Typography.Title>
              <TextAreaField name="standardization" rows={10} />
            </Col>
          </DefaultRow>
          <Row gutter={24}>
            <Col md={24} align="end">
              <Button
                size="large"
                htmlType="submit"
                type="primary"
                onClick={handleSubmit}
                className={styles.button}
                loading={isSubmitting}
              >
                {t('button.submit')}
              </Button>
            </Col>
          </Row>
          {isSubmitting && (
            <div className={styles.spin}>
              <Spin />
            </div>
          )}
        </Form>
      )}
    </div>
  );
}
